/** importing libraries  **/
import jwtDecode from "jwt-decode";
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import React, { useEffect, useState, lazy, Suspense } from "react";
import {
    Button,
    Checkbox,
    Modal,
    Steps,
    Typography,
    Card,
    Divider,
} from "antd";

/** importing dependencies  **/
import Sidebar from "./components/Sidebar/Sidebar";
import ProtectedRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import P3MSuperAdminRoute from "./components/P3MSuperAdminRoute";
import GoogleCalendarConnectModal from "./components/GoogleCalendarConnectModal/GoogleCalendarConnectModal";
import Loader from "./components/Loader/Loader";
import ErrorBoundary from "./pages/Error";
import { useAppDispatch, useAppSelector } from "./hooks/hooks";
import { useMaxWidth } from "./hooks/useMaxWidth";
import { loaduser, logout, updateLicenseInfo } from "./redux/auth/auth.slice";
import { RootState, store } from "./redux/store";
import AuthUtils from "./utils/auth.utils";
import { formatDateIST } from "./utils/formatDateIST";
import { useModalVisibilityContext } from "./context/context";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority
} from "./utils/toast";
import { userApi } from "./services/apis/user.api";
import { useAICreditSSE, useNotificationSSE } from "./hooks/useAICreditSSE";
import CommandPalette from "./components/CommandPalette/CommandPalette";
import KeyboardShortcutsModal from "./components/KeyboardShortcutsModal/KeyboardShortcutsModal";
import useKeyboardShortcut from "./hooks/useKeyboardShortcut";
import { togglePalette } from "./redux/search/search.slice";
import { openShortcuts } from "./redux/ui/ui.slice";
import { SchoolAssessmentRouteShell } from "./pages/SchoolAssessment/SchoolAssessmentRouteShell";
import AuthEntryRoute from "./components/AuthEntryRoute";
import { useFeatureAccessControl } from "./hooks/useFeatureAccessControl";
import { buildLoginPath } from "./utils/returnPath";

// Lazy-loaded page components
const Post = lazy(() => import("./pages/Post/Post"));
const PostDetail = lazy(() => import("./pages/Post/PostDetail/PostDetail"));
const Awaaz = lazy(() => import("./pages/Awaaz/Awaaz"));
const Calendar = lazy(() => import("./pages/Calendar/Calendar"));
const Home = lazy(() => import("./pages/Home/Home"));
const Login = lazy(() => import("./pages/Login/Login"));
const Register = lazy(() => import("./pages/Register/Register"));
const ResourceHub = lazy(() => import("./pages/ResourceHub/ResourceHub"));
const TaskAndGoals = lazy(() => import("./pages/TaskAndGoals/TaskAndGoals"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const DepartmentAndUnits = lazy(() => import("./pages/DepartmentAndUnits/DepartmentAndUnits"));
const UserTaskAndGoals = lazy(() => import("./pages/TaskAndGoals/UserFeedback/UserTask&Goals"));
const AllEmployees = lazy(() =>
    import("./pages/AllEmployees/AllEmployees").catch((err) => {
        if (import.meta.env.DEV) {
            // Transient HMR / dev server chunk glitches: retry once
            // eslint-disable-next-line no-console
            console.warn("AllEmployees: chunk load failed, retrying once", err);
        }
        return import("./pages/AllEmployees/AllEmployees");
    })
);
const AdminResource = lazy(() => import("./pages/ResourceHub/AdminResource/AdminResource"));
const Notice = lazy(() => import("./pages/Notice/Notice"));
const OwnerLogin = lazy(() => import("./pages/OwnerLogin/OwnerLogin"));
const OwnerRegister = lazy(() => import("./pages/OwnerRegister/OwnerRegister"));
const Organization = lazy(() => import("./pages/Organization/Organization"));
const OrganizationDetails = lazy(() => import("./pages/Organization/OrganizationDetails"));
const ResetPassword = lazy(() => import("./pages/ResetPassword/ResetPassword"));
const ResetPasswordLink = lazy(() => import("./pages/ResetPasswordLink/ResetPasswordLink"));
const OwnerDashboard = lazy(() => import("./pages/Dashboard/OwnerDashboard"));
const AdminDashboard = lazy(() => import("./pages/Dashboard/AdminDashboard"));
const TicketManagement = lazy(() => import("./pages/Tickets/Tickets"));
const Groups = lazy(() => import("./pages/Groups/Groups"));
const AuditeeDashboard = lazy(() => import("./pages/Dashboard/AuditeeDashboard/AuditeeDashboard"));
const Audits = lazy(() => import("./pages/Audits/Audits"));
const OneTimeAudits = lazy(() => import("./pages/Audits/OneTimeAudits/OneTimeAudits"));
const AuditShortLinkWrapper = lazy(() => import("./pages/Audits/AuditShortLinkWrapper"));
const KPIs = lazy(() => import("./pages/KPIs/KPIs"));
const TemplateMarketplace = lazy(
    () => import("./pages/TemplateMarketplace/TemplateMarketplace")
);
const DepartmentDashboard = lazy(() => import("./pages/DepartmentUnitDashboard/DepartmentUnitDashboard"));
const MyDashboard = lazy(() => import("./pages/Dashboard/MyDashboard"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const Notifications = lazy(() => import("./pages/Notifications/Notifications"));
const CouponManager = lazy(() => import("./pages/CouponManager/CouponManager"));
const AnecdotalRecord = lazy(() => import("./pages/AnecdotalRecord/AnecdotalRecord"));
const AuditCategories = lazy(() => import("./pages/AuditCategories/AuditCategories"));
const AIDashboard = lazy(() => import("./pages/AIAnalytics/AIDashboard"));
const AIAudits = lazy(() => import("./pages/AIAudits/AIAudits"));
const RegisterOrganizationSuperAdmin = lazy(() => import("./pages/P3MSuperAdmin/RegisterOrganizationSuperAdmin"));
const ImageVerification = lazy(() => import("./pages/ImageVerification/ImageVerification"));
const BillingDashboard = lazy(() => import("./pages/Billing/BillingDashboard"));
// Direct-to-consumer: the buyer's take and report pages (public) and the ops console (P3M staff).
const TakeTestPage = lazy(() => import("./pages/D2C/TakeTestPage"));
const MyReportPage = lazy(() => import("./pages/D2C/MyReportPage"));
const MyInvoicePage = lazy(() => import("./pages/D2C/MyInvoicePage"));
const D2COpsRoutes = lazy(() => import("./pages/D2COps/D2COpsRoutes"));
// Development-only gallery of the buyer pages' states; never part of a production build.
const D2CPreviewPage = import.meta.env.DEV ? lazy(() => import("./pages/D2C/D2CPreviewPage")) : null;

const AssessmentsPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.AssessmentsPage })));
const CreateAssessmentPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.CreateAssessmentPage })));
const EditAssessmentPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.EditAssessmentPage })));
const AssessmentDetailPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.AssessmentDetailPage })));
const TestTakerPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.TestTakerPage })));
const SchoolAssessmentEntry = lazy(() => import("./pages/SchoolAssessment/SchoolAssessmentEntry"));
const SchoolAssessmentDirectEntry = lazy(() => import("./pages/SchoolAssessment/SchoolAssessmentDirectEntry"));
const StudentTaskEntry = lazy(() => import("./pages/StudentTasks/StudentTaskEntry"));
const SubOrganizationDetail = lazy(() => import("./pages/Evaluations/SubOrganizationDetail"));
const ReportDetailPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.ReportDetailPage })));
const QuestionBankPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.QuestionBankPage })));
const AssessmentAnalyticsPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.AssessmentAnalyticsPage })));
const StudentProgressPage = lazy(() => import("./pages/Assessment").then(m => ({ default: m.StudentProgressPage })));
const QuestionBankManagementPage = lazy(() => import("./pages/Assessment/QuestionBankManagementPage"));
const QuestionPapersPage = lazy(() => import("./pages/Assessment/QuestionPapersPage"));
const QuestionPaperBuilderPage = lazy(() => import("./pages/Assessment/QuestionPaperBuilderPage"));
const QuestionPaperPreviewPage = lazy(() => import("./pages/Assessment/QuestionPaperPreviewPage"));
const WorkshopCertificatesPage = lazy(() => import("./pages/WorkshopCertificates/WorkshopCertificatesPage"));
const WorkshopDetailPage = lazy(() => import("./pages/WorkshopCertificates/WorkshopDetailPage"));
const CertificateDesignerPage = lazy(() => import("./pages/WorkshopCertificates/designer/DesignerPage"));

const StudentManagement = lazy(() => import("./pages/Evaluations/StudentManagement").then(m => ({ default: m.StudentManagement })));
const SharedReportPage = lazy(() => import("./pages/Evaluations/SharedReport"));
const CertificateClaimPage = lazy(() => import("./pages/CertificateClaim/CertificateClaimPage"));
const CertificateVerifyPage = lazy(() => import("./pages/CertificateClaim/CertificateVerifyPage"));
const TeenInsightReportPage = lazy(() => import("./pages/TeenInsight/TeenInsightReportPage"));
const WellbeingCohortReportPage = lazy(() => import("./pages/Assessment/WellbeingCohortReportPage"));
const FamilyReportPage = lazy(() => import("./pages/TeenInsight/FamilyReportPage"));
const EvaluationManagement = lazy(() => import("./pages/Evaluations/EvaluationManagement").then(m => ({ default: m.EvaluationManagement })));
const EvaluationDetails = lazy(() => import("./pages/Evaluations/EvaluationDetails").then(m => ({ default: m.EvaluationDetails })));
const IndividualStudentReport = lazy(() => import("./pages/Evaluations/IndividualStudentReport"));
const AggregateReport = lazy(() => import("./pages/Evaluations/AggregateReport").then(m => ({ default: m.AggregateReport })));
const MyEvaluationReport = lazy(() => import("./pages/Evaluations/MyEvaluationReport"));
const StudentEvaluationEntry = lazy(() => import("./pages/Evaluations/StudentEvaluationEntry").then(m => ({ default: m.StudentEvaluationEntry })));
const ScriptShapingCheck = lazy(() => import("./pages/Evaluations/ScriptShapingCheck"));

const { Step } = Steps;
const { Title, Paragraph, Text } = Typography;

/**
 * Utility to return EULA text as a plain string
 */
function getEulaTextAsString(
    effectiveDate: string,
    organizationLocation: string
): string {
    return `
END USER LICENSE AGREEMENT (EULA)
Effective Date: ${effectiveDate}

INTRODUCTION
This End User License Agreement (“Agreement”) is a legal contract between you (User) and Company located in ${organizationLocation} regarding the use of the IMPACT platform (“Software”). By installing, accessing, or using the Software, you acknowledge that you have read, understood, and agree to be bound by the terms of this Agreement.

DEFINITIONS
- Confidential Information: Proprietary and trade secret information designated as confidential.
- Personal Data: Any information relating to an identified or identifiable natural person.
- Processing: Any operation performed on Personal Data, including collection, storage, use, disclosure, and deletion.

LICENSE GRANT
The Company grants you a non-exclusive, non-transferable, revocable license to use the Software solely for your internal business or personal purposes, in accordance with the terms set forth herein.

SUBSCRIPTION AND TRIAL PERIOD
- Trial Period: 30-day free trial from the date of initial access.
- Subscription: Post-trial, continued access requires paid subscription per our official pricing.

USE RESTRICTIONS
- You shall not modify, reverse engineer, decompile, or disassemble the Software.
- You shall not rent, lease, loan, sublicense, or distribute the Software.
- You shall not use the Software for any unlawful purpose.

DATA PROTECTION AND PRIVACY
The Company collects, processes, and stores Personal Data in accordance with applicable global data protection laws. By using the Software, you consent to such collection and use as outlined in our Privacy Policy.

DATA CONFIDENTIALITY
All documents and data uploaded by Users for performance auditing are treated as confidential and protected by industry-standard security measures. Access is restricted to authorized personnel only.

SECURITY MEASURES
The Company employs administrative, physical, and technical safeguards to protect Personal Data against unauthorized access, disclosure, or destruction.

GRIEVANCE REDRESSAL MECHANISM
The Company has appointed a Grievance Officer to address user complaints related to data protection and privacy. Contact at [Grievance Officer Name / Email].

INTELLECTUAL PROPERTY
The Software, including all intellectual property rights, remains the property of the Company.

TERMINATION
Your rights under this Agreement terminate automatically if you fail to comply with any term. Upon termination, you must cease all use and destroy all copies.

DISCLAIMER OF WARRANTIES
The Software is provided “as is” without warranties of any kind, express or implied.

LIMITATION OF LIABILITY
The Company shall not be liable for any indirect or consequential damages arising out of or related to your use of the Software.

GOVERNING LAW AND DISPUTE RESOLUTION
This Agreement is governed by the laws of ${organizationLocation}. Disputes are resolved via arbitration in ${organizationLocation}.

AMENDMENTS
The Company reserves the right to modify this Agreement at any time. Continued use after changes constitutes acceptance of those changes.
`.trim();
}

/**
 * Utility to return role-specific text as a plain string
 */
function getRoleSpecificAgreementTextAsString(
    userName: string,
    userRoles: string[],
    organizationName: string,
    organizationId: string | undefined
): string {
    // Format an "Effective Date" portion
    const effectiveDate = formatDateIST(new Date(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

    // You can expand or modify these strings to suit your needs
    if (
        userRoles.includes("SuperAdmin") ||
        userRoles.includes("P3MSuperAdmin")
    ) {
        return `
SUPER ADMIN USER LICENSE AGREEMENT
Effective Date: ${effectiveDate}

By accessing or using the IMPACT platform, I, ${userName} ("Super Admin") agree to the following:
1. I am authorized to create an organization and assume full responsibility for its setup and configuration.
2. I agree to pay applicable subscription fees on behalf of the organization.
3. I acknowledge that all data generated within this organization will be under my oversight.
4. I am responsible for inviting and managing Admins, Auditees, and External Auditors.
5. I confirm that I will use the software in accordance with all terms in the IMPACT End User License Agreement and Privacy Policy.

ORGANIZATION CREATION AGREEMENT
Effective Date: ${effectiveDate}

By proceeding to create an organization on IMPACT, I, ${userName} ("Super Admin") acknowledge:
1. I am legally authorized to represent and act on behalf of the organization.
2. This organization will be governed under the terms of the IMPACT platform.
3. I accept responsibility for organization-level configurations and compliance.
        `.trim();
    } else if (userRoles.includes("Admin")) {
        return `
ADMIN USER LICENSE AGREEMENT
Effective Date: ${effectiveDate}

By accessing the IMPACT platform as an Admin, I, ${userName}, agree that:
1. I have been added to Organization ${organizationName} (ID: ${organizationId || "N/A"}) by the Super Admin.
2. I will perform configuration, user oversight, and task/audit allocation as per organizational goals.
3. I will comply with the terms of the End User License Agreement and all data confidentiality standards.
        `.trim();
    } else if (userRoles.includes("Auditee")) {
        return `
AUDITEE USER LICENSE AGREEMENT
Effective Date: ${effectiveDate}

By accepting this agreement, I, ${userName}, acknowledge that:
1. I am a registered Auditee under Organization ${organizationName} (ID: ${organizationId || "N/A"}).
2. I will participate in audits and may be required to submit anecdotal records or respond to tasks.
3. I shall respect data confidentiality and platform rules during all processes.
        `.trim();
    } else if (userRoles.includes("Auditor")) {
        return `
EXTERNAL AUDITOR USER LICENSE AGREEMENT
Effective Date: ${effectiveDate}

By accepting this agreement, I, ${userName}, hereby accept the invitation extended by Super Admin of Organization ${organizationName} (ID: ${organizationId || "N/A"}) to perform external auditory services on IMPACT.
1. I agree to conduct audits objectively and professionally within the defined scope.
2. I will respect the confidentiality and privacy of all organizational data.
        `.trim();
    } else {
        // A fallback message for roles that don't match the above
        return `
(No special role disclaimers found for your assigned role.)
        `.trim();
    }
}

/** A wrapper for your main protected routes **/
function Content(): JSX.Element {
    const isMobile = useMaxWidth(768);
    return (
        <div
            style={{
                overflowY: "auto",
                height: "100vh",
                width: isMobile ? "100vw" : "auto",
            }}
        >
            <Routes>
                <Route path="*" element={<NotFoundPage />} />
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Home />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/calendar"
                    element={
                        <ProtectedRoute>
                            <Calendar />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/audits/:tab?"
                    element={
                        <ProtectedRoute>
                            <Audits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/audits/:id"
                    element={
                        <ProtectedRoute>
                            <Audits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/audits/:tab/:id"
                    element={
                        <ProtectedRoute>
                            <Audits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/t/:shortLink"
                    element={
                        <ProtectedRoute>
                            <TaskAndGoals />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/one-time-audits"
                    element={
                        <ProtectedRoute>
                            <OneTimeAudits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/task-and-goals/:tab?"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <TaskAndGoals />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/department-dashboard"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <DepartmentDashboard />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/parameter-settings"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <KPIs />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/template-marketplace"
                    element={
                        <ProtectedRoute>
                            <TemplateMarketplace />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/user-task-and-goals/:tab?"
                    element={
                        <ProtectedRoute>
                            <UserTaskAndGoals />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/user-task-and-goals/:id"
                    element={
                        <ProtectedRoute>
                            <UserTaskAndGoals />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ut/:shortLink"
                    element={
                        <ProtectedRoute>
                            <UserTaskAndGoals />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/awaaz"
                    element={
                        <ProtectedRoute>
                            <Awaaz />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile/:id/:name"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/dashboard/:id"
                    element={
                        <ProtectedRoute>
                            <AuditeeDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/resource-hub"
                    element={
                        <ProtectedRoute>
                            <ResourceHub />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/socials"
                    element={
                        <ProtectedRoute>
                            <Post />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/socials/:postId"
                    element={
                        <ProtectedRoute>
                            <PostDetail />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tickets"
                    element={
                        <ProtectedRoute>
                            <TicketManagement />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/groups"
                    element={
                        <ProtectedRoute>
                            <Groups />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/notice"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <Notice />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/department-and-unit"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <DepartmentAndUnits />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/all-employees/:tab?"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AllEmployees />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin-resource-hub"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AdminResource />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/owner-dashboard"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <OwnerDashboard />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin-dashboard"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AdminDashboard />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/audit-categories"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AuditCategories />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/coupon-manager"
                    element={
                        <ProtectedRoute>
                            <P3MSuperAdminRoute>
                                <CouponManager />
                            </P3MSuperAdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/register-organization-super-admin"
                    element={
                        <ProtectedRoute>
                            <P3MSuperAdminRoute>
                                <RegisterOrganizationSuperAdmin />
                            </P3MSuperAdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/billing"
                    element={
                        <ProtectedRoute>
                            <P3MSuperAdminRoute>
                                <BillingDashboard />
                            </P3MSuperAdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/d2c/*"
                    element={
                        <ProtectedRoute>
                            <P3MSuperAdminRoute>
                                <D2COpsRoutes />
                            </P3MSuperAdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-dashboard"
                    element={
                        <ProtectedRoute>
                            <MyDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/add-organization"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <Organization />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/organization/:id"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <OrganizationDetails />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/notifications"
                    element={
                        <ProtectedRoute>
                            <Notifications />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/anecdotal-records"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AnecdotalRecord />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                {/* <Route
                    path="/plan-upgrade"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <PlanUpgrade />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                /> */}
                <Route
                    path="/ai-analytics"
                    element={
                        <ProtectedRoute>
                            <AIDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ai-audits"
                    element={
                        <ProtectedRoute>
                            <AIAudits />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/image-verification/:tab?"
                    element={
                        <ProtectedRoute>
                            <ImageVerification />
                        </ProtectedRoute>
                    }
                />
                {/* Assessment Routes */}
                <Route
                    path="/assessment"
                    element={<Navigate to="/assessments" replace />}
                />
                <Route
                    path="/assessments"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AssessmentsPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assessments/create"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <CreateAssessmentPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assessments/:id/edit"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <EditAssessmentPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assessments/:id"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AssessmentDetailPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assessments/:id/wellbeing-report"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <WellbeingCohortReportPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/test-attempts/:attemptId/report"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ReportDetailPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/question-bank"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionBankPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/question-banks/manage"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionBankManagementPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/question-papers"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionPapersPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/question-papers/:id"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionPaperBuilderPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/question-papers/:id/preview"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionPaperPreviewPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/papers/preview/:token"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <QuestionPaperPreviewPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                {/* No AdminRoute: the pages decide access themselves (P3M
                    staff or an org admin, in an org with the switch on) and
                    show a "not available" state to everyone else. */}
                <Route
                    path="/workshop-certificates"
                    element={
                        <ProtectedRoute>
                            <WorkshopCertificatesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/workshop-certificates/:id"
                    element={
                        <ProtectedRoute>
                            <WorkshopDetailPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/workshop-certificates/:id/design"
                    element={
                        <ProtectedRoute>
                            <CertificateDesignerPage />
                        </ProtectedRoute>
                    }
                />
                {/* No AdminRoute: the server allows P3M staff and the pair's
                    organisation admins, and the page shows its own state to
                    anyone else. */}
                <Route
                    path="/teen-insight/:pairId"
                    element={
                        <ProtectedRoute>
                            <TeenInsightReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/test-attempts/:attemptId"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ReportDetailPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assessment-analytics/:assessmentId"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AssessmentAnalyticsPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/student-progress"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <StudentProgressPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />

                {/* Evaluation Routes */}
                <Route
                    path="/evaluation-students"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <StudentManagement />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <EvaluationManagement />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations/:id"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <EvaluationDetails />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations/:id/students/:studentId/report"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <IndividualStudentReport />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations/:id/aggregate-report"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AggregateReport />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations/sub-org/:subOrgId"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <SubOrganizationDetail />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/me/evaluations/:evaluationId/report"
                    element={
                        <ProtectedRoute>
                            <MyEvaluationReport />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/evaluations/script-shaping-check"
                    element={
                        <ProtectedRoute>
                            <ScriptShapingCheck />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </div>
    );
}

/**
 * Public pages a signed-in visitor may also open: a workshop certificate, a
 * Teen Insight 360 family report, and a direct-to-consumer take, report or
 * invoice link. The license wizard and the calendar prompt stay out of the way there.
 */
const isPublicVisitPage = (pathname: string): boolean =>
    pathname.startsWith("/certificate/") ||
    pathname.startsWith("/family-report/") ||
    pathname.startsWith("/take/") ||
    pathname.startsWith("/my-report/") ||
    pathname.startsWith("/my-invoice/");

function App() {
    const isAuthenticated = useAppSelector(
        (state: RootState) => state.auth.isAuthenticated
    );
    const token = useAppSelector((state: RootState) => state.auth.access_token);
    const user = useAppSelector((state: RootState) => state.auth.user);
    const selectedOrganization = useAppSelector(
        (state: RootState) => state.organization.selectedOrganization
    );
    const { calendarModalVisible, setCalendarModalVisible } =
        useModalVisibilityContext();
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Step wizard states
    const [showWizard, setShowWizard] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    // Track acceptance for each step
    const [acceptedSteps, setAcceptedSteps] = useState([false, false]);

    useAICreditSSE();
    useNotificationSSE();

    // Feature access control - checks current route against organization features
    useFeatureAccessControl();

    /**
     * EULA content as React node (for display).
     * We also store it as plain text for updating profile.
     */
    const eulaStep = (
        <Card
            style={{ background: "#f7f7f7", borderRadius: 8 }}
            variant="borderless"
        >
            <Title level={4} style={{ marginBottom: 16, color: "#fa8c16" }}>
                End User License Agreement (EULA)
            </Title>
            <Divider />
            <Paragraph strong>
                <Text style={{ color: "#fa8c16" }}>Effective Date:</Text>{" "}
                {formatDateIST(new Date(), {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                })}
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Introduction
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                This End User License Agreement (“Agreement”) is a legal
                contract between you (<Text strong>{user?.name || "User"}</Text>
                ) and{" "}
                <Text strong>{selectedOrganization?.name || "Company"}</Text>{" "}
                located in{" "}
                <Text strong>
                    {selectedOrganization?.location || "Your Location"}
                </Text>{" "}
                regarding the use of the IMPACT platform (“Software”). By
                installing, accessing, or using the Software, you acknowledge
                that you have read, understood, and agree to be bound by the
                terms of this Agreement.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Definitions
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                <Text strong>Confidential Information:</Text> Proprietary and
                trade secret information designated as confidential.
                <br />
                <Text strong>Personal Data:</Text> Any information relating to
                an identified or identifiable natural person.
                <br />
                <Text strong>Processing:</Text> Any operation performed on
                Personal Data, including collection, storage, use, disclosure,
                and deletion.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                License Grant
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company grants you a non-exclusive, non-transferable,
                revocable license to use the Software solely for your internal
                business or personal purposes, in accordance with the terms set
                forth herein.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Subscription and Trial Period
            </Title>
            <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                <li>
                    <Text strong>Trial Period:</Text> 30-day free trial from the
                    date of initial access.
                </li>
                <li>
                    <Text strong>Subscription:</Text> Post-trial, continued
                    access requires paid subscription per our official pricing.
                </li>
            </ul>

            <Title level={5} style={{ marginTop: 16 }}>
                Use Restrictions
            </Title>
            <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                <li>
                    You shall not modify, reverse engineer, decompile, or
                    disassemble the Software.
                </li>
                <li>
                    You shall not rent, lease, loan, sublicense, or distribute
                    the Software.
                </li>
                <li>
                    You shall not use the Software for any unlawful purpose.
                </li>
            </ul>

            <Title level={5} style={{ marginTop: 16 }}>
                Data Protection and Privacy
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company collects, processes, and stores Personal Data in
                accordance with applicable global data protection laws. By using
                the Software, you consent to such collection and use as outlined
                in our Privacy Policy.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Data Confidentiality
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                All documents and data uploaded by Users for performance
                auditing are treated as confidential and protected by
                industry-standard security measures. Access is restricted to
                authorized personnel only.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Security Measures
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company employs administrative, physical, and technical
                safeguards to protect Personal Data against unauthorized access,
                disclosure, or destruction.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Grievance Redressal Mechanism
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company has appointed a Grievance Officer to address user
                complaints related to data protection and privacy. Contact at
                [Grievance Officer Name / Email].
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Intellectual Property
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Software, including all intellectual property rights,
                remains the property of the Company.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Termination
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                Your rights under this Agreement terminate automatically if you
                fail to comply with any term. Upon termination, you must cease
                all use and destroy all copies.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Disclaimer of Warranties
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Software is provided “as is” without warranties of any kind,
                express or implied.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Limitation of Liability
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company shall not be liable for any indirect or
                consequential damages arising out of or related to your use of
                the Software.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Governing Law and Dispute Resolution
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                This Agreement is governed by the laws of{" "}
                <Text strong>
                    {selectedOrganization?.location || "Jurisdiction"}
                </Text>
                . Disputes are resolved via arbitration in{" "}
                <Text strong>
                    {selectedOrganization?.location || "Location"}
                </Text>
                .
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>
                Amendments
            </Title>
            <Paragraph style={{ lineHeight: 1.7 }}>
                The Company reserves the right to modify this Agreement at any
                time. Continued use after changes constitutes acceptance of
                those changes.
            </Paragraph>

            <Divider />
            <Checkbox
                checked={acceptedSteps[0]}
                onChange={(e) => {
                    const newArr = [...acceptedSteps];
                    newArr[0] = e.target.checked;
                    setAcceptedSteps(newArr);
                }}
            >
                I have read and accept this End User License Agreement
            </Checkbox>
        </Card>
    );

    /**
     * Role-specific content as React node (for display).
     */
    function roleSpecificAgreementText(): React.ReactNode {
        const effectiveDate = formatDateIST(new Date(), {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

        if (
            user?.role?.includes("SuperAdmin") ||
            user?.role?.includes("P3MSuperAdmin")
        ) {
            return (
                <>
                    <Title level={4}>Super Admin User License Agreement</Title>
                    <Paragraph>Effective Date: {effectiveDate}</Paragraph>
                    <Paragraph>
                        By accessing or using the IMPACT platform, I,{" "}
                        {user?.name} ("Super Admin") agree to the following:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>
                                I am authorized to create an organization and
                                assume full responsibility for its setup and
                                configuration.
                            </li>
                            <li>
                                I agree to pay applicable subscription fees on
                                behalf of the organization.
                            </li>
                            <li>
                                I acknowledge that all data generated within
                                this organization will be under my oversight.
                            </li>
                            <li>
                                I am responsible for inviting and managing
                                Admins, Auditees, and External Auditors.
                            </li>
                            <li>
                                I confirm that I will use the software in
                                accordance with all terms in the IMPACT End User
                                License Agreement and Privacy Policy.
                            </li>
                        </ul>
                    </Paragraph>

                    <Title level={4} style={{ marginTop: 16 }}>
                        Organization Creation Agreement
                    </Title>
                    <Paragraph>Effective Date: {effectiveDate}</Paragraph>
                    <Paragraph>
                        By proceeding to create an organization on IMPACT, I,{" "}
                        {user?.name} ("Super Admin") acknowledge:
                    </Paragraph>
                    <Paragraph>
                        <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                            <li>
                                I am legally authorized to represent and act on
                                behalf of the organization.
                            </li>
                            <li>
                                This organization will be governed under the
                                terms of the IMPACT platform.
                            </li>
                            <li>
                                I accept responsibility for organization-level
                                configurations and compliance.
                            </li>
                        </ul>
                    </Paragraph>
                </>
            );
        } else if (user?.role?.includes("Admin")) {
            return (
                <>
                    <Title level={4}>Admin User License Agreement</Title>
                    <Paragraph>Effective Date: {effectiveDate}</Paragraph>
                    <Paragraph>
                        By accessing the IMPACT platform as an Admin, I,{" "}
                        {user?.name}, agree that:
                    </Paragraph>
                    <Paragraph>
                        <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                            <li>
                                I have been added to Organization{" "}
                                {selectedOrganization?.name || "NA"} (ID:{" "}
                                {selectedOrganization?.uId || "NA"}) by the
                                Super Admin.
                            </li>
                            <li>
                                I will perform configuration, user oversight,
                                and task/audit allocation as per organizational
                                goals.
                            </li>
                            <li>
                                I will comply with the terms of the End User
                                License Agreement and all data confidentiality
                                standards.
                            </li>
                        </ul>
                    </Paragraph>
                </>
            );
        } else if (user?.role?.includes("Auditee")) {
            return (
                <>
                    <Title level={4}>Auditee User License Agreement</Title>
                    <Paragraph>Effective Date: {effectiveDate}</Paragraph>
                    <Paragraph>
                        By accepting this agreement, I, {user?.name},
                        acknowledge that:
                    </Paragraph>
                    <Paragraph>
                        <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                            <li>
                                I am a registered Auditee under Organization{" "}
                                {selectedOrganization?.name || "NA"} (ID:{" "}
                                {selectedOrganization?.uId || "NA"}).
                            </li>
                            <li>
                                I will participate in audits and may be required
                                to submit anecdotal records or respond to tasks.
                            </li>
                            <li>
                                I shall respect data confidentiality and
                                platform rules during all processes.
                            </li>
                        </ul>
                    </Paragraph>
                </>
            );
        } else if (user?.role?.includes("Auditor")) {
            return (
                <>
                    <Title level={4}>
                        External Auditor User License Agreement
                    </Title>
                    <Paragraph>Effective Date: {effectiveDate}</Paragraph>
                    <Paragraph>
                        By accepting this agreement, I, {user?.name}, hereby
                        accept the invitation extended by Super Admin of
                        Organization {selectedOrganization?.name || "NA"} (ID:{" "}
                        {selectedOrganization?.uId || "NA"}) to perform external
                        auditory services on IMPACT.
                    </Paragraph>
                    <Paragraph>
                        <ul style={{ paddingLeft: 24, lineHeight: 1.7 }}>
                            <li>
                                I agree to conduct audits objectively and
                                professionally within the defined scope.
                            </li>
                            <li>
                                I will respect the confidentiality and privacy
                                of all organizational data.
                            </li>
                        </ul>
                    </Paragraph>
                </>
            );
        } else {
            return <Paragraph>(No special role disclaimers found.)</Paragraph>;
        }
    }

    const roleStep = (
        <Card
            style={{ background: "#f7f7f7", borderRadius: 8 }}
            variant="borderless"
        >
            <Title level={4} style={{ marginBottom: 16, color: "#fa8c16" }}>
                Role-Specific License Agreement
            </Title>
            <Divider />
            <Paragraph style={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {roleSpecificAgreementText()}
            </Paragraph>
            <Divider />
            <Checkbox
                checked={acceptedSteps[1]}
                onChange={(e) => {
                    const newArr = [...acceptedSteps];
                    newArr[1] = e.target.checked;
                    setAcceptedSteps(newArr);
                }}
            >
                I, <Text strong>{user?.name}</Text>, accept this role-specific
                agreement for{" "}
                <Text strong>{selectedOrganization?.name || "NA"}</Text>
            </Checkbox>
        </Card>
    );

    // Step definitions for display
    const disclaimersSteps = [
        { title: "End User License Agreement", content: eulaStep },
        { title: "Role Agreement", content: roleStep },
    ];

    /**
     * Attempt updating user's profile once the user has accepted all disclaimers.
     * We'll store the full disclaimers text in "licenseDescription".
     */
    const handleFinishLicenseAcceptance = async () => {
        if (!user?._id) return;

        // Build combined license text in plain format
        const nowDate = formatDateIST(new Date(), {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
        const eulaText = getEulaTextAsString(
            nowDate,
            selectedOrganization?.location || "Your Location"
        );
        const roleText = getRoleSpecificAgreementTextAsString(
            user?.name || "User",
            user?.role || [],
            selectedOrganization?.name || "Company",
            selectedOrganization?.uId
        );
        const combinedLicenseText = `${eulaText}\n\n${roleText}`.trim();

        // Unique ID for tracking this acceptance
        const licenseId = user?.user?.license?._id;

        try {
            const access_token = localStorage.getItem("access_token")
                ? JSON.parse(localStorage.getItem("access_token") ?? "")
                : "";
            if (!access_token) {
                return;
            }
            const header = { Authorization: `Bearer ${access_token}` };
            setLoading(true);
            // Call the updateProfile with licenseAccepted, licenseId & licenseDescription
            const { data } = await userApi.updateProfile(
                user.teacherId,
                {
                    licenseAccepted: true,
                    licenseId,
                    licenseDescription: combinedLicenseText,
                    organizations: [selectedOrganization?._id],
                },
                header
            );
            dispatch(updateLicenseInfo(data.user));
            setLoading(false);
            // Then close the wizard
            setShowWizard(false);
        } catch (err) {
            console.error("Error updating user license acceptance:", err);
            setLoading(false);
        }
    };

    /**
     * Step wizard button navigation
     */
    const handleNext = () => {
        if (!acceptedSteps[currentStep]) {
            // The user hasn't checked "I accept" => do nothing or maybe show a toast
            return;
        }

        if (currentStep < disclaimersSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            // Final "Finish" step
            handleFinishLicenseAcceptance();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleDecline = () => {
        // If user declines => logout and show a toast
        dispatch(logout());
        minimalToast({
            dispatch,
            title: "License Declined",
            message: "You declined the license. Logging out...",
            category: NotificationCategory.ALERT,
            priority: NotificationPriority.HIGH,
        });
        setShowWizard(false);
    };

    // The disclaimers wizard modal
    const disclaimersModal = (
        <Modal
            title="License Agreements"
            open={showWizard}
            onCancel={handleDecline}
            footer={null}
            maskClosable={false}
            closable={false}
            width={860}
        >
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                {disclaimersSteps.map((st, idx) => (
                    <Step key={idx} title={st.title} />
                ))}
            </Steps>

            {disclaimersSteps[currentStep].content}

            <Divider />

            <div style={{ textAlign: "right" }}>
                {currentStep > 0 && (
                    <Button style={{ marginRight: 8 }} onClick={handlePrev}>
                        Previous
                    </Button>
                )}
                <Button style={{ marginRight: 8 }} onClick={handleDecline}>
                    Decline
                </Button>
                {/* Next or Finish */}
                {currentStep < disclaimersSteps.length - 1 && (
                    <Button
                        type="primary"
                        disabled={!acceptedSteps[currentStep]}
                        onClick={handleNext}
                    >
                        Next
                    </Button>
                )}
                {currentStep === disclaimersSteps.length - 1 && (
                    <Button
                        type="primary"
                        disabled={!acceptedSteps[currentStep]}
                        onClick={handleNext}
                    >
                        Finish
                    </Button>
                )}
            </div>
        </Modal>
    );

    // Global ⌘K / Ctrl+K to open the command palette
    useKeyboardShortcut("ctrl+k", () => {
        if (isAuthenticated) {
            dispatch(togglePalette());
        }
    });

    // Global "?" opens the keyboard-shortcuts help overlay - but never while the
    // user is typing in a field (so "?" stays a literal character there).
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "?" || e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            const typing =
                !!t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.isContentEditable);
            if (typing) return;
            if (!isAuthenticated) return;
            e.preventDefault();
            dispatch(openShortcuts());
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isAuthenticated, dispatch]);

    // Check if user is logged in, or redirect
    useEffect(() => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        let userId;

        // Define public routes that don't require authentication
        const publicRoutes = [
            "/login",
            "/register",
            "/owner-login",
            "/owner-register",
            "/forget-password",
        ];
        // The public evaluation routes: the entry screen a student starts on, and
        // the task board they return to afterwards.
        const isEvaluationStartRoute =
            location.pathname.startsWith("/evaluation/") &&
            (location.pathname.endsWith("/start") || location.pathname.endsWith("/tasks"));
        const isWaveYearSchoolRoute = /^\/(BL|EL)\/\d{4}\/[^/]+$/i.test(location.pathname);
        const isPublicRoute = publicRoutes.includes(location.pathname) ||
            location.pathname.startsWith("/reset-password/") ||
            location.pathname.startsWith("/test/") ||
            // A report opened from a share link: the token is the key, no account.
            location.pathname.startsWith("/shared/") ||
            // A workshop certificate link: teachers have no account.
            location.pathname.startsWith("/certificate/") ||
            // A Teen Insight 360 family report link: parents have no account.
            location.pathname.startsWith("/family-report/") ||
            // A bought test and its report: the link in the buyer's message is the key.
            location.pathname.startsWith("/take/") ||
            location.pathname.startsWith("/my-report/") ||
            location.pathname.startsWith("/my-invoice/") ||
            (import.meta.env.DEV && location.pathname.startsWith("/d2c-preview")) ||
            location.pathname.startsWith("/a/") ||
            location.pathname.startsWith("/assess/") ||
            isWaveYearSchoolRoute ||
            isEvaluationStartRoute;

        // Check for invitation token in URL (for anonymous access to one-time audits)
        const queryParams = new URLSearchParams(location.search);
        const invitationToken =
            queryParams.get("token") || queryParams.get("invitationToken");
        const isOneTimeAuditsRoute =
            location.pathname.includes("one-time-audits");
        const isTestRoute = location.pathname.startsWith("/test/");
        const isAnonymousMode = queryParams.get("mode") === "anonymous";
        const hasInvitationToken = Boolean(
            invitationToken && invitationToken.trim().length > 0
        );

        // Allow public access to test routes when mode=anonymous (anonymous users)
        if (isTestRoute && isAnonymousMode && hasInvitationToken) {
            // Don't redirect - allow anonymous access to tests
            return;
        }

        if (token) {
            const decodedToken = jwtDecode<{ _id: string }>(token);
            if ("_id" in decodedToken) {
                userId = decodedToken._id;
            }
        }
        if (token && userId && !access_token) {
            dispatch(loaduser(userId));
        }
        if (access_token && access_token !== "") {
            AuthUtils.setAuthToken();
            // Bootstrap the user profile only once per session. This effect
            // re-runs on every route change (location.pathname is a dependency),
            // and `authorize` dispatches `loaduser`, which fetches
            // /teacher/teacher-details. Without this guard that profile call
            // fired on every page navigation. Once the profile is in the store,
            // there's nothing to reload on navigation.
            if (!store.getState().auth.user) {
                AuthUtils.authorize(store);
            }
        } else {
            // If there's an invitation token on one-time-audits route, allow access
            if (hasInvitationToken && isOneTimeAuditsRoute) {
                // Don't redirect - allow access with invitation token
                return;
            }

            // Don't redirect if user is already on a public route
            if (isPublicRoute) {
                // Just ensure logout state is set, but don't navigate
                dispatch(logout());
                AuthUtils.removeLocalStorage("access_token");
                return;
            }

            dispatch(logout());
            AuthUtils.removeLocalStorage("access_token");
            const emailParam = queryParams.get("email");
            if (!emailParam) {
                // Carry the page along so a successful sign-in returns here
                // (covers session expiry and EULA decline, which only dispatch
                // logout and rely on this effect to move the user).
                navigate(buildLoginPath(location), { replace: true });
            }
        }
    }, [isAuthenticated, location.search, location.pathname]);

    // If user is authenticated, show disclaimers wizard only once (unless they've accepted before)
    useEffect(() => {
        // Not on a public certificate or family report page: a signed-in
        // visitor there is only checking a certificate or reading a report,
        // not using the app.
        if (isAuthenticated && user && !isPublicVisitPage(location.pathname)) {
            // If user has not already accepted the license, we show the wizard
            if (!user.licenseAccepted) {
                setShowWizard(true);
            }
        }
    }, [isAuthenticated, user, location.pathname]);

    // Add effect to show the modal once when needed
    useEffect(() => {
        // Not on a public certificate or family report page, for the same
        // reason as the EULA effect above.
        if (isAuthenticated && user && !user.googleRefreshToken && !isPublicVisitPage(location.pathname)) {
            // Only set the modal to visible if it hasn't been shown before and isn't explicitly set to false in localStorage
            const calendarModalVisibleInStorage = localStorage.getItem(
                "calendarModalVisible"
            );
            if (
                calendarModalVisibleInStorage === null ||
                JSON.parse(calendarModalVisibleInStorage) !== false
            ) {
                // Avoid popping immediately; wait 1.5 s
                const t = setTimeout(() => setCalendarModalVisible(true), 1500);
                return () => clearTimeout(t);
            }
        }
    }, [isAuthenticated, user, location.pathname]);

    const setCalendarModalToFalse = () => {
        setCalendarModalVisible(false);
        localStorage.setItem("calendarModalVisible", JSON.stringify(false));
    };

    return (
        <Loader loading={loading} position="center">
            <ErrorBoundary>
                {/* {disclaimersModal} */}

                {/* Add Google Calendar connect popup */}
                <GoogleCalendarConnectModal
                    visible={calendarModalVisible}
                    onClose={setCalendarModalToFalse}
                />

                {/* Global command palette (⌘K / Ctrl+K) */}
                {isAuthenticated && <CommandPalette />}

                {/* Global keyboard-shortcuts help overlay (?) */}
                {isAuthenticated && <KeyboardShortcutsModal />}

                {/* Wrap the entire UI in SidebarProvider */}
                <Suspense fallback={<Loader loading={true} position="center" />}>
                    <Routes>
                        {/* Sign-in pages: once authenticated, AuthEntryRoute
                            forwards to the page carried in ?redirect= (set by
                            buildLoginPath on every trip here) or the fallback. */}
                        <Route
                            path="/login"
                            element={
                                <AuthEntryRoute fallback="/">
                                    <Login />
                                </AuthEntryRoute>
                            }
                        />
                        <Route
                            path="/owner-login"
                            element={
                                <AuthEntryRoute fallback="/owner-dashboard">
                                    <OwnerLogin />
                                </AuthEntryRoute>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <AuthEntryRoute fallback="/">
                                    <Register />
                                </AuthEntryRoute>
                            }
                        />
                        <Route
                            path="/owner-register"
                            element={
                                <AuthEntryRoute fallback="/">
                                    <OwnerRegister />
                                </AuthEntryRoute>
                            }
                        />
                        <Route
                            path="/forget-password"
                            element={<ResetPasswordLink />}
                        />
                        <Route
                            path="/reset-password/:token"
                            element={<ResetPassword />}
                        />
                        <Route
                            path="/evaluation/:evaluationId/start"
                            element={<StudentEvaluationEntry />}
                        />
                        <Route
                            path="/test/:publicTestId"
                            element={<TestTakerPage />}
                        />
                        <Route
                            path="/certificate/verify/:code"
                            element={<CertificateVerifyPage />}
                        />
                        <Route
                            path="/certificate/:slug"
                            element={<CertificateClaimPage />}
                        />
                        <Route
                            path="/shared/report/:token"
                            element={<SharedReportPage />}
                        />
                        <Route
                            path="/family-report/:token"
                            element={<FamilyReportPage />}
                        />
                        <Route
                            path="/take/:token"
                            element={<TakeTestPage />}
                        />
                        <Route
                            path="/my-report/:token"
                            element={<MyReportPage />}
                        />
                        <Route
                            path="/my-invoice/:token"
                            element={<MyInvoicePage />}
                        />
                        {import.meta.env.DEV && D2CPreviewPage ? (
                            <Route
                                path="/d2c-preview"
                                element={<D2CPreviewPage />}
                            />
                        ) : null}
                        <Route
                            path="/BL/:year/:schoolId"
                            element={
                                <SchoolAssessmentRouteShell>
                                    <SchoolAssessmentEntry fixedWave="BL" />
                                </SchoolAssessmentRouteShell>
                            }
                        />
                        <Route
                            path="/EL/:year/:schoolId"
                            element={
                                <SchoolAssessmentRouteShell>
                                    <SchoolAssessmentEntry fixedWave="EL" />
                                </SchoolAssessmentRouteShell>
                            }
                        />
                        <Route
                            path="/assess/:schoolId/:wave/:year"
                            element={
                                <SchoolAssessmentRouteShell>
                                    <SchoolAssessmentEntry />
                                </SchoolAssessmentRouteShell>
                            }
                        />
                        <Route
                            path="/assess/:schoolId/test/:publicTestId"
                            element={
                                <SchoolAssessmentRouteShell>
                                    <SchoolAssessmentDirectEntry />
                                </SchoolAssessmentRouteShell>
                            }
                        />
                        {/* Public student task boards. Reached from the same link
                            the student took the assessment on. */}
                        <Route
                            path="/assess/:schoolId/tasks"
                            element={
                                <SchoolAssessmentRouteShell>
                                    <StudentTaskEntry />
                                </SchoolAssessmentRouteShell>
                            }
                        />
                        <Route
                            path="/evaluation/:evaluationId/tasks"
                            element={<StudentTaskEntry />}
                        />
                        <Route
                            path="/test/:publicTestId/tasks"
                            element={<StudentTaskEntry />}
                        />
                        <Route
                            path="/a/:shortLink"
                            element={<AuditShortLinkWrapper />}
                        />
                        <Route
                            path="*"
                            element={
                                <Sidebar>
                                    <Content />
                                </Sidebar>
                            }
                        />
                    </Routes>
                </Suspense>
            </ErrorBoundary>
        </Loader>
    );
}

export default App;
